import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { gerarUrlAvatar, gerarUrlBranding } from '@/utils/avatar';
import type { AppRole } from '@/utils/permissions';

export interface BootstrapPerfil {
  id: string;
  email: string;
  telefone: string | null;
  avatar_url: string | null;
  display_name: string | null;
  tenant_id: string | null;
  departamento_id: string | null;
  supervisor_id: string | null;
  ativo: boolean;
}

export interface BootstrapBranding {
  logo_url_horizontal: string | null;
  logo_url_icone: string | null;
  logo_url_alta_resolucao: string | null;
  url_custom_dominio: string | null;
  cores_primarias: { primary?: string; secondary?: string } | null;
  nome_fantasia: string | null;
}

export interface BootstrapMenuItem {
  id: string;
  grupo: string | null;
  label: string;
  icon: string;
  path: string;
  roles_permitidas: string[] | null;
  dynamic_check: string | null;
  ordem_grupo: number;
  ordem_item: number;
}

export interface BootstrapData {
  autenticado: boolean;
  user_id: string | null;
  roles: AppRole[];
  pode_acessar: boolean;
  tenant_id: string | null;
  perfil: BootstrapPerfil | null;
  branding: BootstrapBranding | null;
  menu_items: BootstrapMenuItem[];
}

export const BOOTSTRAP_QUERY_KEY = ['bootstrap-usuario'] as const;

/**
 * Bootstrap unificado: 1 round-trip que devolve roles, perfil, branding,
 * menu e flag de onboarding. Substitui o encadeamento
 *   getUser → get_my_roles → usuarios_perfis → organizacoes → menu_items → fn_usuario_pode_acessar.
 *
 * O resultado é persistido em localStorage (ver App.tsx), de modo que após o
 * primeiro login o reload exibe a UI imediatamente e revalida em background
 * (stale-while-revalidate).
 */
export function useBootstrapUsuario(userId: string | null | undefined) {
  return useQuery<BootstrapData>({
    queryKey: [...BOOTSTRAP_QUERY_KEY, userId ?? null],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<BootstrapData> => {
      const { data, error } = await supabase.rpc('fn_bootstrap_usuario');
      if (error) throw error;

      const raw = (data ?? {}) as Partial<BootstrapData>;
      const perfil = raw.perfil ?? null;
      const branding = raw.branding ?? null;

      // Assina URLs de avatar e logos em paralelo
      const [avatarAssinado, logoH, logoI, logoA] = await Promise.all([
        perfil?.avatar_url ? gerarUrlAvatar(perfil.avatar_url) : Promise.resolve(null),
        branding?.logo_url_horizontal ? gerarUrlBranding(branding.logo_url_horizontal) : Promise.resolve(null),
        branding?.logo_url_icone ? gerarUrlBranding(branding.logo_url_icone) : Promise.resolve(null),
        branding?.logo_url_alta_resolucao ? gerarUrlBranding(branding.logo_url_alta_resolucao) : Promise.resolve(null),
      ]);

      return {
        autenticado: raw.autenticado ?? false,
        user_id: raw.user_id ?? null,
        roles: (raw.roles ?? []) as AppRole[],
        pode_acessar: raw.pode_acessar ?? true,
        tenant_id: raw.tenant_id ?? null,
        perfil: perfil
          ? {
              ...perfil,
              avatar_url: avatarAssinado,
            }
          : null,
        branding: branding
          ? {
              ...branding,
              logo_url_horizontal: logoH,
              logo_url_icone: logoI,
              logo_url_alta_resolucao: logoA,
            }
          : null,
        menu_items: raw.menu_items ?? [],
      };
    },
  });
}

/**
 * Pré-carrega o bootstrap após o login para que o redirect ao /dashboard já
 * encontre o cache pronto e renderize sem cascata de requests.
 */
export async function prefetchBootstrap(queryClient: ReturnType<typeof useQueryClient>, userId: string) {
  await queryClient.prefetchQuery({
    queryKey: [...BOOTSTRAP_QUERY_KEY, userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('fn_bootstrap_usuario');
      if (error) throw error;
      return data;
    },
  });
}

/** Hook utilitário para limpar/invalidar o bootstrap (logout, troca de tenant). */
export function useInvalidarBootstrap() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: BOOTSTRAP_QUERY_KEY });
}

/** Mantém compat: invalida o bootstrap quando algo do menu muda no realtime. */
export function useRealtimeBootstrap(userId: string | null | undefined) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!userId) return;
    const canal = supabase
      .channel('bootstrap-menu-items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'menu_items' }, () => {
        queryClient.invalidateQueries({ queryKey: BOOTSTRAP_QUERY_KEY });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(canal);
    };
  }, [userId, queryClient]);
}
